/**
 * User manual (English) — Phase 4.
 *
 * Mirror of `manual.es.js`. Keep sections aligned 1:1 by `id` so the help
 * page's TOC + search work the same in both languages. When you add a section
 * here, add it to `manual.es.js` first (or vice-versa).
 */

const manual = [
  {
    id: "intro",
    title: "Introduction",
    icon: "BookOpen",
    intro:
      "Distribución Canales Dashboard is a web tool to measure warehouse-employee productivity from Excel reports exported by your ERP.",
    body: `### What does this app do?

It parses Excel files from the ERP and turns them into visual dashboards so you can:

- See **transaction volume per employee** (picks, receipts, transfers, etc.).
- Identify the **Winner of the month** by volume or efficiency (tx/hr).
- Compare employees, departments and periods.
- Spot bottlenecks with views like Heatmap, Trend, Distribution and 3D.
- Share the dashboard on a **TV** or via a **public link with QR**, with no account required.

### Supported report types

| File | Contents |
| --- | --- |
| GENDETAILED_PROD | Detailed transactions (RF PICK, RF STAGE, RF TRNS, RF RCPT, RF.RCVR). |
| IC385DETAIL | Inventory / adjustments (ERP variant). |
| IC385P2DETAIL | P2 variant of IC385. |
| TE350 | **Hours worked** per employee (needed for tx/hr efficiency). |

### Stack

Web-first (React + FastAPI + MongoDB) with a fixed timezone of \`America/Chicago\`. Rows are deduplicated by hash on upload — you can safely re-upload the same file.`,
  },
  {
    id: "acceso",
    title: "Access & sign-in",
    icon: "LogIn",
    body: `### Sign in

1. Open the app — you'll be redirected to the login screen.
2. Enter your email and password.
3. The app routes you to the **first view your role can see** (Dashboard by default). A viewer with limited permissions lands directly on a permitted report — no 403.

### Default admin credentials

- **Email:** \`admin@bodega.com\`
- **Password:** \`Admin123!\`

> On first login the admin must **change this password** at *Administration → Users → your account → Change password*. These credentials are seeded for quick setup but are visible to anyone reading this manual.

### Sign out

Click your avatar (top-right) → **Sign out**. The JWT token is discarded from the browser and you return to login.

### Forgot password?

This version has no self-service reset. Ask the administrator to enter *Users → your account → Change password* and set a new one for you.

### Language and theme

The topbar has two toggles: **Languages (ES/EN)** and **Sun/Moon** (light/dark). Your choice is saved in \`localStorage\` and persists across sessions.`,
    tips: [
      {
        kind: "warning",
        text: "Change the default admin password immediately. \"Admin123!\" is public in this manual.",
      },
    ],
  },
  {
    id: "primer-upload",
    title: "Your first upload",
    icon: "Upload",
    body: `### Steps

1. Click **Upload report** (cloud ↑ icon) in the sidebar. Requires the \`upload\` permission.
2. Drag a \`.xlsx\` file onto the dashed zone **or** click to pick it. Recommended size: up to ~10 MB.
3. **Report type** auto-detects from the filename. If it can't (renamed file), pick it manually.
4. Click **Ingest report**. You'll see "Processing…" then a summary:
   - **Total rows**: valid rows in the Excel.
   - **Inserted**: new rows saved to Mongo.
   - **Duplicates**: rows that already existed (same hash) and were skipped.
   - **Upload ID**: unique identifier of this ingest (persisted in history).
5. TE350 uploads show **Hours inserted / Hours duplicated** instead of transactions.

### How does the dedupe work?

Each row is hashed with SHA-256 over \`(employee_id, transaction_type, timestamp, item, qty, order_id, source_report)\`. Re-uploading the same file shows **0 inserted / N duplicates** — that's the idempotency guarantee.

### System blacklist

Rows with \`employee_id\` containing \`SYSTEM\`, \`CLOCK\`, \`AUCRON\`, \`TEST\`, \`DISPT\`, \`DEFAULT\`, \`STORE OWNER\` are dropped automatically — those are ERP technical entries, not real employees.

### Upload history

*Administration → Uploads history*. Lists every ingest with file, type, user, date, totals and counts. Useful for audits.`,
    tips: [
      {
        kind: "tip",
        text: "If the ERP exports several files per day (GENDETAILED in the morning + TE350 at night), upload them in order. Dedupe merges them with no duplication.",
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: "LayoutDashboard",
    body: `### Global view

\`/dashboard\` is the main screen. It shows:

- **4 KPIs**: Total transactions, Active employees, Top employee, Top type.
- **2 extra KPIs** (if TE350 is uploaded): Avg tx/hr and Top efficient employee.
- **AI message** for safety / productivity (section 12).
- **Featured winner** of the period (initial-avatar, total, "View full" link).
- **Top 10 ranking** (horizontal bar chart by department).
- **Distribution by type** (donut with percentages).
- **Monthly trend** (multi-series lines by transaction type).

### Global filters

Above the Dashboard there's a **Filters** panel. The chosen combination is preserved across reports — the same filter applies app-wide.

Fields:

- **Period (preset)**: Today / Yesterday / This week / This month / This year / Custom.
- **From / To** (Custom only).
- **Source**: limit to a single source report.
- **Transaction types**: multi-select chips.
- **Departments**: multi-select chips (each with its color).
- **Employees**: multi-select (filters after the above).

Click **Reset** to return to the default preset (\`year\`).

### Drill-through

Click the avatar of the Featured Winner → takes you to **/reports/ganador** with the same filter.`,
  },
  {
    id: "ganador",
    title: "Winner of the period",
    icon: "Trophy",
    body: `### Volume vs Efficiency mode

\`/reports/ganador\` offers two modes:

- **Volume** (default): the employee with the most transactions wins.
- **Efficiency tx/hr**: the employee with the best tx/hr ratio wins. **Requires TE350** — without it this mode is empty.

### Period & "anchor"

- **Period**: Day / Week / Month / Year.
- **Anchor to**: empty = auto-detects the busiest period. You can type e.g. \`2026-05\` or \`2026-W22\`.

### 3D Gold / Silver / Bronze podium

Below the highlighted winner there's a podium of 3 pure-CSS 3D pillars showing the **Top 3** with medal badges and totals. Hover for a subtle lift effect.

### Top 3 by transaction type

5 mini-podiums (one per \`RF PICK / RF STAGE / RF TRNS / RF RCPT / RF.RCVR\`) showing the Top 3 employees for that specific type.

### Confetti

When there's a winner, a CSS-only confetti animation appears over the winner card. It's decorative — no perf impact.`,
    tips: [
      {
        kind: "tip",
        text: "If you need \"who moves the most boxes per hour?\", switch to Efficiency mode. If roles differ a lot per employee (picker vs receiver), filter by transaction type to compare apples to apples.",
      },
    ],
  },
  {
    id: "ranking",
    title: "Ranking",
    icon: "Award",
    body: `### Top employees

\`/reports/ranking\` shows a **horizontal bar chart stacked by transaction type**. Each bar is an employee, segments show how much each type contributes.

### Controls

- **Top N**: 5, 10, 20 or 50 employees (default 20).
- Bars are colored by department.

### Drill-through

**Click an employee name** (or any bar) → takes you to **/reports/comparativo?compare=Z21** with that employee preselected.`,
  },
  {
    id: "comparativo",
    title: "Comparative",
    icon: "Scale",
    body: `### Compare employees side by side

\`/reports/comparativo\` lets you pick **up to 10 employees** and view them in a grouped bar chart by transaction type.

Steps:

1. Click employees on the picker (each becomes a chip).
2. Watch the chart update live.
3. Once you reach 10, the rest are disabled.

### Coming from Ranking

If you arrive via drill-through (\`?compare=Z21\`), that employee is preselected. Add more to compare.`,
  },
  {
    id: "tendencia",
    title: "Trend",
    icon: "TrendingUp",
    body: `### Temporal evolution

\`/reports/tendencia\` graphs transaction count over time.

### Controls

- **Granularity**: Daily / Weekly / Monthly.
- **Series by**: Transaction type (default — one line per type), Employee or Department. If you get many series, narrow with the department filter first.`,
  },
  {
    id: "distribucion",
    title: "Distribution by type",
    icon: "PieChart",
    body: `\`/reports/distribucion\` shows a donut/pie with the share of each transaction type.

Each slice has a fixed color (Tokyo-Night palette: pink for RF PICK, mustard RF STAGE, green RF TRNS, cyan RF RCPT, purple RF.RCVR) plus a percentage. Hover to see the absolute count.`,
  },
  {
    id: "heatmap",
    title: "Heatmap employee × day",
    icon: "Flame",
    body: `\`/reports/heatmap\` shows a table with employees on rows and weekdays on columns. The cell is colored by intensity (monochrome teal scale). The max value in the filter shows in the header as reference.

Useful to spot:

- Who only works Monday-Thursday.
- Who carries the full week.
- "Dead" days in the warehouse.`,
  },
  {
    id: "tabla",
    title: "Detailed table",
    icon: "TableProperties",
    body: `### Raw data

\`/reports/tabla\` lists individual transactions with pagination. Use this when you need the actual row — e.g. "what item did Diana move at 2:23 PM on May 18?".

### Local filters

- Full-text search by employee, item or order.
- Page size: 25 / 50 / 100 / 200.

### Export

- **Excel (.xlsx)** or **CSV** with the filtered result.
- Safety cap: **500 rows** per export to avoid memory blowups. Need more? Refine filters.`,
    tips: [
      {
        kind: "warning",
        text: "Export is capped at 500 rows per request. It's intentional — 100k rows in one Excel crashes the browser. Apply filters by month or department.",
      },
    ],
  },
  {
    id: "eficiencia",
    title: "Efficiency tx/hr",
    icon: "Zap",
    body: `### tx per hour worked

\`/reports/efficiency\` combines transactions with TE350 hours to compute **tx/hr** per employee.

### Visualizations

- **3 KPIs**: Avg tx/hr, Top efficient employee, Total hours.
- **Scatter** (Hours vs Transactions, bubble size = tx/hr) lets you spot quadrants — many hours but low volume sit bottom-right, super-efficient ones top-left.
- **Table** with the ranking sorted by tx/hr.

### Why is it empty?

If the view says "No hours recorded", **the TE350 is missing**. Go to *Upload report → TE350*. Efficiency recomputes automatically on the next refresh.`,
  },
  {
    id: "vista3d",
    title: "3D View",
    icon: "Boxes",
    body: `\`/reports/3d\` offers three Plotly 3D visualizations:

### Visualization types

- **3D Bars**: top N employees × transaction types → height = volume.
- **3D Scatter**: employees × weekday × volume, bubble size = log(count).
- **Surface**: top N employees × weekdays → continuous relief. Great to spot patterns.

### Controls

- **Top N** (10, 20, 30, 50).
- **Reset camera** (back to initial view).
- **Download PNG** (1600 × 900).
- **Fullscreen**.

### Performance

Plotly is loaded **on demand** (~3 MB) the first time you visit. Subsequent visits are instant.`,
  },
  {
    id: "empleados-canonicos",
    title: "Canonical employees",
    icon: "Combine",
    body: `### The problem

The ERP sometimes records the same person under multiple codes. Diana Arellano appears as \`Z21\` in GENDETAILED and \`089\` in IC385. Unmerged, they show as two people in the ranking.

### The fix: canonical employees

\`/admin/employees\` groups every code of the same person under a **canonical** entity identified by normalized name.

### Operations

- **Merge**: combines aliases of employee B into canonical A and removes B.
- **Split**: when two real people were merged by mistake (same name, different person), move selected aliases to a new canonical.
- **Rebuild index**: re-process every transaction to detect new aliases. Run after uploading a brand-new Excel where names changed.

### Bulk delete (soft)

Select several → click **Delete selected**. They're **marked inactive**, but no transaction is deleted. They show an "Inactive" badge if you check **Show inactive**. To reactivate, click → **Reactivate employee**.

> Historical data is never lost: deleting a canonical only hides it from the views. Reports respect the inactive flag.`,
  },
  {
    id: "departamentos",
    title: "Departments",
    icon: "Building2",
    body: `\`/admin/departments\` groups Z## employees into departments for grouped reports.

### Steps

1. **Add department** → name, color (hex), confirm.
2. Click the card → **Add code (Z##)** → type the code and enter.
3. The department color is automatically used in charts (ranking bars, podium, etc.).
4. Edit / Delete from the three-dots menu.

### Permissions

Requires \`dept:manage\`. Viewers see colors but can't modify.`,
  },
  {
    id: "usuarios-permisos",
    title: "Users & permissions",
    icon: "Users",
    body: `\`/admin/users\` manages access. Admin only.

### Roles

- **admin**: full access, all permissions.
- **viewer**: restricted to the permissions you assign.

### Available permissions

| Permission | What it unlocks |
| --- | --- |
| \`upload\` | Upload Excel reports |
| \`users:manage\` | Manage users and permissions |
| \`dept:manage\` | Manage departments |
| \`employees:manage\` | Canonical employees (merge / split / delete) |
| \`share:manage\` | Create and manage public links |
| \`report:kpis\` | View Dashboard KPIs |
| \`report:ranking\` | View Ranking |
| \`report:ganador\` | View Winner of the month |
| \`report:comparativo\` | View Comparative |
| \`report:tendencia\` | View Trend |
| \`report:distribucion\` | View Distribution |
| \`report:heatmap\` | View Heatmap |
| \`report:tabla\` | View Table |
| \`report:efficiency\` | View Efficiency tx/hr |
| \`report:3d\` | View 3D View |

### Create a viewer

1. Click **Add user**.
2. Email + name + password.
3. Role = \`viewer\`.
4. Tick the permissions they need (at least one, otherwise they see nothing useful).
5. Assign departments if you want to restrict their data scope.

### Change password

Row → three dots → **Change password**. Type the new one, enter. The user must re-sign-in with the new password.

### Deactivate

Click 🗑 → confirm. The account is inactive but not deleted. Reactivate from the same screen by clearing the filter.`,
    tips: [
      {
        kind: "tip",
        text: "A floor supervisor can have just report:ganador + report:ranking, no upload, no admin. They see their monthly podium without risk of breaking data.",
      },
    ],
  },
  {
    id: "links-publicos",
    title: "Public links",
    icon: "Share2",
    body: `\`/admin/share\` creates public URLs (no login) to share a **read-only** dashboard.

### 4 scopes

| Scope | Use case |
| --- | --- |
| **Corporate** | Full view of the whole operation. |
| **Department** | Filter to one or more departments. |
| **Supervisor** | Employees of the departments a specific supervisor manages. |
| **Employee** | One or more specific employees. |

### Create a link

1. Pick the scope (icon card).
2. Select departments / employees / supervisor as needed.
3. **Period**: pre-applied time filter (Today, Month, Year…).
4. **Label** optional, for your records (e.g. "Main floor TV").
5. **Expires in (days)** optional — empty means never.
6. **PIN** optional — protect the link with a numeric code.
7. **Views to include** — multi-select chips of views the public viewer can see (default: all).
8. **Default view** — which one to show first.
9. Click **Generate link**. A modal pops with the URL — copy it or open directly.

### Per-link actions

- **Copy** the URL.
- **Open** in a new tab.
- **TV mode** → opens with \`?mode=tv&slides=1\`.
- **Slides** → auto-rotation without fullscreen.
- **QR** → generates a downloadable QR code PNG (perfect to print and stick on a wall).
- **Email** → opens your mail client with a prefilled message.
- **Revoke** → disables without deleting (badge "Revoked").
- **Delete** → permanent removal.

### Bulk cleanup

Three buttons for mass cleanup: **Delete revoked / Delete expired / Delete ALL history**. Useful for periodic housekeeping.

### Security

- The link **requires no login**. Anyone with it can view.
- If you set a **PIN**, it's required before showing the dashboard; a 15-min JWT is kept in \`sessionStorage\` so a refresh doesn't re-prompt.
- Public endpoints are **rate-limited to 60 hits/min/IP**.
- The view counter increments once per minute/IP/token.

### Redacted data

Before returning to the public viewer, the backend strips internal Mongo IDs and admin fields. Visitors can't see \`canonical_id\`, \`department_id\` or routes outside \`views_to_include\`.`,
    tips: [
      {
        kind: "warning",
        text: "Anyone with the link can view it. Treat the URL like a \"magic link\". Use a PIN when sharing outside the organization.",
      },
    ],
  },
  {
    id: "modo-tv",
    title: "TV / Slides mode",
    icon: "Tv",
    body: `### Setting up a warehouse screen

1. Create a public link with **Corporate** scope (or any other you prefer) and include the views you want to rotate.
2. Click **TV mode** → opens the URL with \`?mode=tv&slides=1&interval=20\`.
3. On the TV browser (Chrome / Edge), ideally launch in **kiosk mode**:

\`\`\`
chrome --kiosk --noerrdialogs --disable-infobars LINK_URL
\`\`\`

4. The app enters fullscreen automatically (or if the browser blocks it, click the **Fullscreen** button).
5. Every \`interval\` seconds (default 20), the view rotates (Dashboard → Winner → Ranking → Trend → Distribution → Heatmap → Efficiency → loop).
6. To exit: move the mouse to the bottom-right → click **Exit** (Maximize2 icon).

### Available parameters

| Parameter | Values | Default |
| --- | --- | --- |
| \`mode\` | \`tv\` | (no TV) |
| \`slides\` | \`1\` | off |
| \`interval\` | seconds (min 5) | 20 |
| \`theme\` | \`dark\` / \`light\` | dark |
| \`lang\` | \`es\` / \`en\` | browser preference |

### QR for employees

In the link card, click **QR** → modal with the code. Download the PNG and **stick it on a wall** in the warehouse. Employees scan with their phones and see the dashboard with nothing to install.`,
  },
  {
    id: "ai-mensajes",
    title: "AI safety messages",
    icon: "ShieldAlert",
    body: `### What are they?

The **AI messages** cards on the Dashboard and Winner are short AI-generated phrases (Emergent LLM, \`gpt-4o-mini\`) that rotate. Three categories:

- **Safety** 🛡️ — PPE reminders, safe lifting, etc.
- **Productivity** ⚡ — tips to improve tx/hr.
- **Info** 💡 — curious / motivational facts.

### Controls

- **Another tip** — forces a re-fetch (generates new if none cached).
- **Auto-rotate** — toggle on/off; rotates every ~30 s.
- **AI generated / Pre-seeded message** — small hint of origin. If the LLM fails, a seeded fallback phrase is used.

### Language

Messages are generated in the active language (\`es\` or \`en\`). If you flip the toggle, the next message arrives already translated.

> If you don't want to spend LLM credit, the system works the same with pre-loaded phrases — you only notice because the hint reads "Pre-seeded message".`,
  },
  {
    id: "i18n-tema",
    title: "Language & theme",
    icon: "Languages",
    body: `### Language

**ES / EN** toggle in the topbar. Internally uses \`react-i18next\` and the choice is saved in \`localStorage.muleria_lang\`.

It covers **the entire UI**: labels, error messages, weekdays, months, tooltips, this very manual and the AI messages.

### Theme

**Sun / Moon** toggle in the topbar. **Dark** by default (Tokyo-Night palette). The choice is saved in \`localStorage.muleria_theme\` and applied via the \`dark\` class on \`<html>\`.

> Both toggles are also on the login page and the shared public view.`,
  },
  {
    id: "faq",
    title: "FAQ",
    icon: "HelpCircle",
    body: `### Why does an employee show up twice?

Their aliases weren't unified. Go to *Administration → Canonical employees*, find the duplicate and click **Merge**.

### Why don't I see "Efficiency tx/hr"?

Either (a) **no TE350 file is uploaded** (no hours → no tx/hr) or (b) **your account lacks** \`report:efficiency\`. Ask the admin to add it.

### Why does the upload say 0 inserted and N duplicates?

You re-uploaded the same file. Dedupe by hash detected those rows already existed. Not an error — it's the idempotency guarantee.

### Why does the public link say "expired"?

You set *Expires in (days)* when creating it. After that date the link enters **expired** state and returns 410 on open. Fix: regenerate (links **don't** auto-renew).

### How do I project the dashboard on a TV?

Create a corporate public link, click **TV mode**, and open the URL in Chrome kiosk on the TV. See "TV mode".

### Can I change timezone or currency?

No. The app uses **America/Chicago** (Texas-based client). No currency — just counts.

### What if an Excel comes with different columns?

The parser tries to infer the type from the filename. If columns don't match the schema, it returns 422 with details. Fixes: (a) re-export from the ERP, (b) rename the file to a recognizable one, (c) use the manual "Report type" override.

### Do employees with misspelled names merge automatically?

No. Unification is **by code** (Z##, 089, TE350), not by name. If two transactions for the same person have different codes that aren't mapped, you'll see two people. Use **Merge** manually.

### How do I block an employee from the ranking?

Go to *Canonical employees*, select them, click **Delete selected**. They become inactive and disappear from all reports. Reactivable anytime.

### Can I export the podium as an image?

- **3D View**: yes, **Download PNG** (1600×900) button.
- **Other podiums**: no built-in export. Use OS screenshot or your browser's print (Ctrl/Cmd + P).

### Why isn't "Public links" in my sidebar?

You lack \`share:manage\`. Ask the admin.

### Can I plug in Slack / Teams notifications?

It's in the backlog (P1) but not implemented. The app does **not** send notifications — everything is on-demand on URL open.`,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: "Wrench",
    body: `### "Invalid token" / "401 Unauthorized"

Your JWT expired (24 h lifetime). Sign in again.

### "Unsupported file"

Only \`.xlsx\`. If you export to old \`.xls\` or CSV, convert first. If the correct file errors out, share the offending column with the admin (the ERP might have changed schema).

### "Wrong PIN" on a public link

Double-check the PIN with whoever generated the link. After 5 failed attempts in a minute the rate-limiter blocks you for one minute (60 hits/min/IP).

### "Too many requests" / 429

The public endpoint rate-limits to **60 hits/min/IP**. Wait a minute. If you see this often, you're probably behind a NAT with many clients — use another link or ask the admin for one with lower rotation.

### Fullscreen doesn't kick in (TV mode)

Some browsers block \`requestFullscreen()\` without a user gesture. Fix: on the TV, open the link, move the mouse → click **Fullscreen** manually. **Or** launch Chrome with \`--kiosk\`.

### Empty data / view

Check the **global filters** at the top of the Dashboard. If you pick *Today* and nobody uploaded anything today, you'll see empty. Click **Reset** to go back to the \`year\` preset.

### Charts look cropped / re-render badly

Refresh the page (\`Ctrl/Cmd + R\`). It's a Recharts quirk when the window resizes very fast.

### 3D Plotly won't load

First load downloads ~3 MB. If your network is slow, wait. If you see a console error like \`Cannot find module 'plotly.js-dist-min'\`, alert the admin — a dep is missing.`,
  },
  {
    id: "soporte",
    title: "Support",
    icon: "LifeBuoy",
    body: `### Need more help?

If this manual doesn't answer your question:

- **Report a bug** or **request a feature**: email \`<TODO: support-email@muleria-canales.com>\` with:
  - Screenshot (URL visible).
  - Steps to reproduce.
  - Your role and permissions.
- **In-house training**: contact \`<TODO: internal-IT-name>\`.
- **Locked out** (lost password, inactive account): talk to the system administrator.

> The admin edits this section in code (\`src/help/manual.es.js\` and \`manual.en.js\`). Replace the \`<TODO>\` placeholders with your real contact info before sharing the manual with your team.`,
  },
];

export default manual;
